// 공개 화면(첫 화면·요금제·약관) 머리·바닥 한 벌 — 로그인 상태면 「내 현장으로」, 아니면 로그인·데모 보기·무료로 시작하기.

import { Menu } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import { DEMO_PATH } from "@buildpy/core/modules/demo/routes";
import { HERO, LANDING_TAGLINE } from "@buildpy/core/modules/landing/content";
import { LOGIN_PATH } from "@buildpy/core/modules/onboarding/rules";
import { Button } from "@buildpy/ui/button";
import { BrandLogo } from "@buildpy/ui/brand-logo";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@buildpy/ui/sheet";
import { Link } from "@/lib/nav";
import { cn } from "@/lib/utils";
import { APP_CTA, PRICING_PATH, PRIVACY_PATH, SIGNUP_CTA, TERMS_PATH } from "@/modules/plan/copy";
import { LANDING_NAV, PUBLIC_CONTAINER, PUBLIC_NAV } from "./public-header-state";

const NAV_LINK = "inline-flex min-h-11 items-center whitespace-nowrap px-2 text-sm text-secondary-foreground hover:text-primary lg:px-3 lg:text-md";
const SHEET_LINK = "flex min-h-12 items-center px-5 text-lg text-foreground hover:bg-muted";
const FOOT_LINK = "inline-flex min-h-11 items-center text-secondary-foreground hover:text-primary";

// 폰 메뉴 시트 안 항목 — 주 메뉴 · 데모 보기 · (로그인 전) 로그인. 고르면 onPick 으로 시트를 닫는다.
export const MobileMenuItems = ({ signedIn, onLanding, onPick }: { signedIn: boolean; onLanding: boolean; onPick?: () => void }) => (
  <>
    <nav aria-label="메뉴" className="flex flex-col py-2">
      {(onLanding ? LANDING_NAV : PUBLIC_NAV).map((item) => (
        <Link key={item.href} className={SHEET_LINK} href={item.href} onClick={onPick}>
          {item.label}
        </Link>
      ))}
    </nav>
    <div className="flex flex-col gap-2 border-t border-border px-5 py-4">
      <Button asChild className="min-h-11" variant="secondary">
        <Link data-testid="landing-sheet-demo" href={DEMO_PATH.home} onClick={onPick}>
          {HERO.demo}
        </Link>
      </Button>
      {!signedIn && (
        <Button asChild className="min-h-11" variant="ghost">
          <Link href={LOGIN_PATH} onClick={onPick}>
            로그인
          </Link>
        </Button>
      )}
    </div>
  </>
);

// 폰(md 미만) 메뉴 — 링크로 닫을 때는 이동한 곳에 포커스를 두도록 햄버거로 되돌리지 않는다.
const MobileMenu = ({ signedIn, onLanding }: { signedIn: boolean; onLanding: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);
  const pickedRef = useRef(false);
  const pick = () => {
    pickedRef.current = true;
    setIsOpen(false);
  };
  const onCloseAutoFocus = (event: Event) => {
    if (!pickedRef.current) return;
    pickedRef.current = false;
    event.preventDefault();
  };
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button aria-label="메뉴 열기" className="size-11 md:hidden [&_svg]:size-6" size="icon" variant="ghost-muted">
          <Menu />
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-72 flex-col gap-0 p-0" side="right" onCloseAutoFocus={onCloseAutoFocus}>
        {/* 시트 조각에는 안전 영역 클래스를 못 얹어(no-restyle) 빈 줄로 상태바 아래로 내린다. */}
        <div aria-hidden className="shrink-0 pt-safe" />
        <SheetHeader isClose className="px-5" variant="sticky">
          <SheetTitle>
            <BrandLogo />
          </SheetTitle>
          <SheetDescription className="sr-only">이동할 곳을 고릅니다</SheetDescription>
        </SheetHeader>
        {/* 낮은 가로 화면에서도 아래 로그인까지 닿게 머리 밑 본문만 스크롤한다 — 닫기 단추는 머리에 남는다 */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <MobileMenuItems onLanding={onLanding} signedIn={signedIn} onPick={pick} />
        </div>
      </SheetContent>
    </Sheet>
  );
};

// 내려도 위에 붙어 있다(앱 껍데기에선 pt-safe 로 상태바 아래) — 첫 화면 앵커 구간은 이 높이(min-h-16 + 선 + 안전 영역)만큼 scroll-mt-safe 를 둔다 · container 는 본문 폭이 다른 화면(무료 도구)이 머리 가장자리를 본문에 맞출 때만 넘긴다.
export const PublicHeader = ({ signedIn, onLanding = false, container = PUBLIC_CONTAINER }: { signedIn: boolean; onLanding?: boolean; container?: string }) => (
  <header className="sticky top-0 z-20 border-b border-border bg-card pt-safe">
    <div className={cn(container, "flex min-h-16 items-center justify-between gap-3")}>
      <Link className="flex min-h-11 shrink-0 items-center" href="/">
        <BrandLogo />
      </Link>
      <nav aria-label="주 메뉴" className="hidden min-w-0 items-center md:flex">
        {(onLanding ? LANDING_NAV : PUBLIC_NAV).map((item) => (
          <Link key={item.href} className={NAV_LINK} href={item.href}>
            {item.label}
          </Link>
        ))}
      </nav>
      <div className="flex shrink-0 items-center gap-1 md:gap-2">
        {!signedIn && (
          <Button asChild className="hidden min-h-11 md:inline-flex" variant="ghost">
            <Link href={LOGIN_PATH}>로그인</Link>
          </Button>
        )}
        {/* md~lg 는 주 메뉴가 로그인 밑으로 넘쳐 머리 데모는 lg 부터 — 그 구간은 히어로 데모 버튼이 남는다 */}
        <Button asChild className="hidden min-h-11 lg:inline-flex" variant="secondary">
          <Link data-testid="landing-header-demo" href={DEMO_PATH.home}>
            {HERO.demo}
          </Link>
        </Button>
        {signedIn ? (
          <Button asChild className="min-h-11">
            <Link data-testid="landing-app-link" href={APP_CTA.href}>
              {APP_CTA.label}
            </Link>
          </Button>
        ) : (
          <Button asChild className="min-h-11">
            <Link data-testid="landing-header-signup" href={SIGNUP_CTA.href}>
              {SIGNUP_CTA.label}
            </Link>
          </Button>
        )}
        <MobileMenu onLanding={onLanding} signedIn={signedIn} />
      </div>
    </div>
  </header>
);

export const PublicFooter = ({ signedIn, toolsHref = "/tools" }: { signedIn: boolean; toolsHref?: string }) => (
  <footer className="border-t border-border bg-card">
    <div className={cn(PUBLIC_CONTAINER, "flex flex-wrap items-center justify-between gap-x-6 gap-y-3 py-8 text-md")}>
      <div className="flex flex-col items-start gap-1.5">
        <BrandLogo size="sm" />
        <span className="text-muted-foreground">{LANDING_TAGLINE}</span>
      </div>
      <nav aria-label="바닥 메뉴" className="flex flex-wrap gap-x-4">
        <Link className={FOOT_LINK} href={toolsHref}>
          무료 도구
        </Link>
        <Link className={FOOT_LINK} href={DEMO_PATH.home}>
          고객 화면 데모
        </Link>
        <Link className={FOOT_LINK} href={PRICING_PATH}>
          요금제
        </Link>
        <Link className={FOOT_LINK} data-testid="footer-terms" href={TERMS_PATH}>
          이용약관
        </Link>
        <Link className={FOOT_LINK} data-testid="footer-privacy" href={PRIVACY_PATH}>
          개인정보 처리방침
        </Link>
        {!signedIn && (
          <Link className={FOOT_LINK} href={LOGIN_PATH}>
            로그인
          </Link>
        )}
      </nav>
    </div>
  </footer>
);

// 요금제·약관 같은 공개 한 장짜리 화면 틀 — 머리 · 본문 · 바닥.
export const PublicLayout = ({ signedIn, children }: { signedIn: boolean; children: ReactNode }) => (
  <div className="flex min-h-screen flex-col break-keep bg-background text-foreground">
    <PublicHeader signedIn={signedIn} />
    <main className={cn(PUBLIC_CONTAINER, "flex-1 py-12 md:py-16")}>{children}</main>
    <PublicFooter signedIn={signedIn} />
  </div>
);
